import { INK } from '../../Theme/palette';
import { ApprovalServiceSnapshot, Customer, OrderApprovalHistory } from '@services';
import { Utils } from '@utils';
import React, { forwardRef } from 'react';
import { toCamelCase } from './OrderApprovalHistory.columns';

interface IProps {
  record?: OrderApprovalHistory;
  tenantName?: string;
  customer?: Customer;
  customerFullName?: string;
  registrationNumber?: string;
  vehicleModel?: string;
  mileage?: number;
}

const headerCell: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#555',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  padding: '6px 8px',
  borderBottom: '1px solid #888',
  textAlign: 'left',
};

const bodyCell: React.CSSProperties = {
  fontSize: 11,
  padding: '6px 8px',
  borderBottom: '1px solid #ddd',
  verticalAlign: 'top',
};

const subCell: React.CSSProperties = {
  ...bodyCell,
  color: '#666',
  paddingLeft: 20,
};

const totalLine: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '4px 0',
  fontSize: 12,
};

export const EstimatePrintContent = forwardRef<HTMLDivElement, IProps>(
  ({ record, tenantName, customer, customerFullName, registrationNumber, vehicleModel, mileage }, ref) => {
    let services: ApprovalServiceSnapshot[] = [];
    if (record?.snapshot) {
      try {
        services = toCamelCase(JSON.parse(record.snapshot));
      } catch {
        services = [];
      }
    }

    const labourPrice = services.reduce(
      (sum, s) =>
        sum +
        Utils.calculateLabourPrice({
          isFixed: s.isFixed,
          price: s.price || 0,
          discount: s.discount || 0,
          duration: s.duration || 1,
        }),
      0
    );

    const allExtras = services.flatMap((s) => s.extras ?? []);

    const partsPrice = allExtras.reduce((sum, e) => {
      const pt = (e.price || 0) * (e.quantity || 0);
      return sum + pt - (pt * (e.discount || 0)) / 100;
    }, 0);

    const serviceVat = services.reduce(
      (sum, s) =>
        sum +
        Utils.calculateVat({
          isFixed: s.isFixed,
          price: s.price || 0,
          discount: s.discount || 0,
          duration: s.duration || 1,
          vat: s.vat || 0,
        }),
      0
    );

    const partsVat = allExtras.reduce(
      (sum, e) =>
        sum +
        Utils.calculateVat({
          price: e.price || 0,
          discount: e.discount || 0,
          duration: e.quantity || 0,
          vat: e.vat || 0,
        }),
      0
    );

    const vatAmount = serviceVat + partsVat;
    const total = labourPrice + partsPrice + vatAmount;

    const customerName = customer?.fullname || customerFullName;
    const cityPostcode = [customer?.city, customer?.postcode].filter(Boolean).join(', ');
    const customerLines = [
      customer?.businessName,
      customer?.address,
      customer?.secondAddress,
      customer?.thirdAddress,
      cityPostcode,
      customer?.country,
      customer?.email,
      customer?.phone,
      customer?.landline,
    ].filter((line) => line && line.trim());

    return (
      <div ref={ref} style={{ padding: 24, color: '#000', fontFamily: 'Arial, Helvetica, sans-serif' }}>
        <style>{`
          @media print {
            @page { size: A4; margin: 12mm; }
          }
        `}</style>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: `2px solid ${INK}`,
            paddingBottom: 8,
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: INK }}>Estimate</div>
            {tenantName && <div style={{ fontSize: 12 }}>{tenantName}</div>}
          </div>
          <div style={{ textAlign: 'right', fontSize: 12 }}>
            {registrationNumber && (
              <div>
                <strong>Reg:</strong> {registrationNumber}
              </div>
            )}
            {vehicleModel && (
              <div>
                <strong>Vehicle:</strong> {vehicleModel}
              </div>
            )}
            {mileage != null && (
              <div>
                <strong>Mileage:</strong> {mileage.toLocaleString('en-GB')}
              </div>
            )}
            <div>
              <strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}
            </div>
          </div>
        </div>

        {(customerName || customerLines.length > 0) && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#555',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
                marginBottom: 4,
              }}
            >
              Customer
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5 }}>
              {customerName && <div style={{ fontWeight: 600 }}>{customerName}</div>}
              {customerLines.map((line, i) => (
                <div key={`cust-line-${i}`}>{line}</div>
              ))}
            </div>
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={headerCell}>Name</th>
              <th style={{ ...headerCell, width: 70 }}>Type</th>
              <th style={{ ...headerCell, width: 90, textAlign: 'right' }}>Price</th>
              <th style={{ ...headerCell, width: 70, textAlign: 'right' }}>Qty</th>
              <th style={{ ...headerCell, width: 70, textAlign: 'right' }}>Disc</th>
              <th style={{ ...headerCell, width: 60, textAlign: 'right' }}>VAT</th>
              <th style={{ ...headerCell, width: 90, textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s, i) => {
              const svcTotal = Utils.calculateLabourPrice({
                isFixed: s.isFixed,
                price: s.price || 0,
                discount: s.discount || 0,
                duration: s.duration || 1,
              });
              return (
                <React.Fragment key={`svc-${i}`}>
                  <tr>
                    <td style={{ ...bodyCell, fontWeight: 600 }}>{s.title}</td>
                    <td style={bodyCell}>{s.isFixed ? 'Fixed' : 'Labour'}</td>
                    <td style={{ ...bodyCell, textAlign: 'right' }}>{Utils.formatCurrency(s.price || 0)}</td>
                    <td style={{ ...bodyCell, textAlign: 'right' }}>
                      {!s.isFixed ? `${s.duration || 0}h` : '—'}
                    </td>
                    <td style={{ ...bodyCell, textAlign: 'right' }}>{s.discount || 0}%</td>
                    <td style={{ ...bodyCell, textAlign: 'right' }}>{s.vat || 0}%</td>
                    <td style={{ ...bodyCell, textAlign: 'right', fontWeight: 600 }}>
                      {Utils.formatCurrency(svcTotal)}
                    </td>
                  </tr>
                  {s.extras?.map((e, j) => {
                    const pt = (e.price || 0) * (e.quantity || 0);
                    const ptWithDiscount = pt - (pt * (e.discount || 0)) / 100;
                    return (
                      <tr key={`ext-${i}-${j}`}>
                        <td style={subCell}>{e.name}</td>
                        <td style={subCell}>{e.type || 'Part'}</td>
                        <td style={{ ...subCell, textAlign: 'right' }}>
                          {Utils.formatCurrency(e.price || 0)}
                        </td>
                        <td style={{ ...subCell, textAlign: 'right' }}>{e.quantity || 0}</td>
                        <td style={{ ...subCell, textAlign: 'right' }}>{e.discount || 0}%</td>
                        <td style={{ ...subCell, textAlign: 'right' }}>{e.vat || 0}%</td>
                        <td style={{ ...subCell, textAlign: 'right' }}>
                          {Utils.formatCurrency(ptWithDiscount)}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {services.some((s) => s.note && s.note.trim()) && (
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#555',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
                marginBottom: 4,
              }}
            >
              Notes
            </div>
            {services
              .filter((s) => s.note && s.note.trim())
              .map((s, i) => (
                <div key={`note-${i}`} style={{ fontSize: 11, lineHeight: 1.5, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{s.title}: </span>
                  <span style={{ color: '#333', whiteSpace: 'pre-wrap' }}>{s.note}</span>
                </div>
              ))}
          </div>
        )}

        <div style={{ width: 280, marginLeft: 'auto' }}>
          <div style={totalLine}>
            <span>Labour</span>
            <span>{Utils.formatCurrency(labourPrice)}</span>
          </div>
          <div style={totalLine}>
            <span>Parts &amp; Fluids</span>
            <span>{Utils.formatCurrency(partsPrice)}</span>
          </div>
          <div style={totalLine}>
            <span>VAT</span>
            <span>{Utils.formatCurrency(vatAmount)}</span>
          </div>
          <div style={{ ...totalLine, borderTop: '1px solid #888', marginTop: 4, paddingTop: 8, fontWeight: 700 }}>
            <span>TOTAL</span>
            <span>{Utils.formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    );
  }
);

EstimatePrintContent.displayName = 'EstimatePrintContent';

export default EstimatePrintContent;
